import { KeyboardEvent, useEffect, useState } from "react";
import axios from "axios";
import { ClipLoader } from "react-spinners";
import {
  PaperAirplaneIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import auth from "../../fire";
import PageHeader from "../../components/PageHeader/PageHeader";
import BasicSelect from "../../components/BasicSelect/BasicSelect";

const apiBaseURL = process.env.REACT_APP_API_URL || "";

// Dedicated axios instance for the endpoint tester. It mirrors axiosClient's
// auth (Firebase token + userId) but intentionally has NO global-error / Sentry
// interceptor, so every response — including 4xx/5xx and network failures —
// surfaces inline on this page instead of triggering the app-wide error overlay.
const testerClient = axios.create({ baseURL: apiBaseURL });
testerClient.interceptors.request.use(async (config) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // No token available — send anyway (the endpoint will likely 401).
  }
  config.params = config.params || {};
  config.params["userId"] = auth.currentUser?.uid;
  return config;
});

const METHOD_OPTIONS = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
];

const STORAGE_KEY = "customEndpointTester";

interface SavedState {
  method: string;
  path: string;
  body: string;
}

// Request the tester opens with on a fresh load (no saved state yet): the
// admin "copy program" endpoint, pre-filled with its required body fields
// ready to be edited.
const DEFAULT_STATE: SavedState = {
  method: "POST",
  path: "/api/admin/copy-program",
  body: JSON.stringify(
    { programId: "<source program _id>", destinationUserId: "<firebase uid>" },
    null,
    2
  ),
};

interface ApiResult {
  status: number;
  statusText: string;
  durationMs: number;
  sizeLabel: string;
  bodyText: string;
}

const loadSaved = (): SavedState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        method:
          typeof parsed.method === "string"
            ? parsed.method
            : DEFAULT_STATE.method,
        path:
          typeof parsed.path === "string" ? parsed.path : DEFAULT_STATE.path,
        body: typeof parsed.body === "string" ? parsed.body : DEFAULT_STATE.body,
      };
    }
  } catch {
    // Ignore malformed/unavailable localStorage.
  }
  return DEFAULT_STATE;
};

const prettify = (data: unknown): string => {
  if (data === null || data === undefined) return "";
  if (typeof data === "string") return data;
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const statusBadgeClasses = (status: number): string => {
  if (status >= 200 && status < 300) return "bg-green-100 text-green-800";
  if (status >= 300 && status < 400) return "bg-blue-100 text-blue-800";
  if (status >= 400 && status < 500) return "bg-amber-100 text-amber-800";
  return "bg-red-100 text-red-800";
};

const CustomEndpoint = () => {
  const [method, setMethod] = useState<string>(() => loadSaved().method);
  const [path, setPath] = useState<string>(() => loadSaved().path);
  const [body, setBody] = useState<string>(() => loadSaved().body);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const methodHasBody = method !== "GET";

  // Persist the request builder so it survives reloads during development.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ method, path, body }));
    } catch {
      // Ignore unavailable localStorage.
    }
  }, [method, path, body]);

  const handleSend = async () => {
    if (loading || !path.trim()) return;
    setError(null);

    let data: unknown = undefined;
    if (methodHasBody && body.trim()) {
      try {
        data = JSON.parse(body);
      } catch {
        setError("Request body is not valid JSON.");
        return;
      }
    }

    setLoading(true);
    setResult(null);
    const start = performance.now();
    try {
      const res = await testerClient.request<unknown>({
        method,
        url: path.trim(),
        data,
        validateStatus: () => true,
      });
      const durationMs = Math.round(performance.now() - start);
      const bodyText = prettify(res.data);
      setResult({
        status: res.status,
        statusText: res.statusText,
        durationMs,
        sizeLabel: formatBytes(new Blob([bodyText]).size),
        bodyText,
      });
    } catch (err) {
      const durationMs = Math.round(performance.now() - start);
      const message =
        axios.isAxiosError(err) || err instanceof Error
          ? err.message
          : String(err);
      setError(`Request failed (${durationMs} ms): ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFormatBody = () => {
    if (!body.trim()) return;
    try {
      setBody(JSON.stringify(JSON.parse(body), null, 2));
      setError(null);
    } catch {
      setError("Request body is not valid JSON.");
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.bodyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable — no-op.
    }
  };

  return (
    <>
      <PageHeader backLink="/admin-dashboard" pageTitle="Call Custom Endpoint" />

      <div className="h-[calc(100%-63px)] overflow-y-auto bg-gray-50/50 p-4 space-y-4">
        <p className="text-sm text-gray-500">
          Quickly call your backend during development. Your auth token and{" "}
          <span className="font-mono">userId</span> are attached automatically.
        </p>

        {/* Request builder */}
        <div className="bg-white border border-gray-200 rounded-md p-4 space-y-3">
          <div className="flex gap-2">
            <div className="w-32 shrink-0">
              <BasicSelect
                id="tester-method"
                label="Method"
                initialSelectedOption={method}
                options={METHOD_OPTIONS}
                onSelect={(option) => setMethod(option.value)}
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor="tester-path"
                className="block mb-1 text-sm font-medium text-gray-700"
              >
                Endpoint
              </label>
              <input
                id="tester-path"
                type="text"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="/api/admin/copy-program"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm font-mono"
              />
            </div>
          </div>

          {/* Resolved request preview */}
          <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs font-mono text-gray-600 break-all">
            <span className="font-semibold text-gray-800">{method}</span>{" "}
            {apiBaseURL ? `${apiBaseURL}${path}` : path}
            {!apiBaseURL && (
              <span className="text-amber-600">
                {" "}
                (REACT_APP_API_URL not set)
              </span>
            )}
          </div>

          {methodHasBody && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label
                  htmlFor="tester-body"
                  className="block text-sm font-medium text-gray-700"
                >
                  Request body (JSON)
                </label>
                <button
                  type="button"
                  onClick={handleFormatBody}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                >
                  Format
                </button>
              </div>
              <textarea
                id="tester-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={6}
                spellCheck={false}
                placeholder={'{\n  "key": "value"\n}'}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm font-mono"
              />
            </div>
          )}

          <button
            type="button"
            onClick={handleSend}
            disabled={loading || !path.trim()}
            className="group relative flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:pointer-events-none"
          >
            {loading ? (
              <ClipLoader color="white" speedMultiplier={0.6} size={"1.25rem"} />
            ) : (
              <>
                <PaperAirplaneIcon className="h-4 w-4" />
                Send Request
              </>
            )}
          </button>
          <p className="text-center text-xs text-gray-400">
            ⌘ / Ctrl + Enter to send
          </p>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 whitespace-pre-wrap">
              {error}
            </div>
          )}
        </div>

        {/* Response */}
        {result && (
          <div className="bg-white border border-gray-200 rounded-md p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClasses(
                    result.status
                  )}`}
                >
                  {result.status}
                  {result.statusText ? ` ${result.statusText}` : ""}
                </span>
                <span className="text-xs text-gray-500">
                  {result.durationMs} ms · {result.sizeLabel}
                </span>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                {copied ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-green-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <pre className="max-h-80 overflow-auto rounded-md bg-gray-50 border border-gray-200 p-3 text-xs font-mono text-gray-800 whitespace-pre-wrap break-words">
              {result.bodyText || "(empty response body)"}
            </pre>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomEndpoint;
